import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './Dungeon.css';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { authButtonStyle, authButtonSmallStyle } from '../constants/authStyles';
import { translateGameName } from '../utils/gameNameI18n';
import Coachmark from './tutorial/Coachmark';
import { normalizeLanguage } from '../i18n/languages';

const pub = (p) => (p ? `/${String(p).replace(/^\//, '')}` : '');

/** Zindan menüsü: 10/20/30/40 seviye zindanları, günlük hak ve giriş. */
const Dungeon = () => {
  const { t, i18n } = useTranslation('dungeon');
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');

  const load = useCallback(async (keepError = false) => {
    try {
      const res = await axios.get('/api/dungeon/list');
      if (res.data?.success) {
        setData(res.data);
        if (!keepError) setError('');
      } else {
        setError(res.data?.message || t('loadFailed'));
      }
    } catch (e) {
      setError(e.response?.data?.message || t('loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    if (!localStorage.getItem('username')) {
      navigate('/login');
      return;
    }
    load();
  }, [load, navigate]);

  const enter = async (key) => {
    if (busyKey) return;
    setBusyKey(key);
    setError('');
    try {
      const res = await axios.post('/api/dungeon/enter', { key });
      if (res.data?.success) {
        navigate('/battle', { state: { dungeon: key } });
        return;
      }
      setError(res.data?.message || t('enterFailed'));
      load(true);
    } catch (e) {
      setError(e.response?.data?.message || t('enterFailed'));
    } finally {
      setBusyKey('');
    }
  };

  const isEn = normalizeLanguage(i18n.language) !== 'tr';

  return (
    <div className="auth-container auth-container--selection">
      <AuthLayeredCard className="dungeon-card">
        <AuthSelectionToolbar
          onBack={() => navigate('/game')}
          buttonStyle={authButtonStyle}
          header={(
            <>
              <h1>
                <img src="/image/menu/zindan.png" alt="" className="dungeon-title-icon" draggable={false} />
                {t('title')}
              </h1>
              <p>{t('subtitle')}</p>
            </>
          )}
        />
        {error ? <p className="dungeon-error" role="alert">{error}</p> : null}
        {data ? (
          <>
            <p className="dungeon-ruh">
              <img src="/image/market/ruh_parasi.png" alt="" />
              {t('ruh', { ruh: data.ruhParasi })}
            </p>
            {data.activeRunId ? (
              <div className="dungeon-active">
                <span>{t('active')}</span>
                <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => navigate('/battle', { state: { dungeon: 'active' } })}>
                  {t('continue')}
                </button>
              </div>
            ) : null}
            <Coachmark
              tourId="dungeon"
              steps={[{ selector: '.dungeon-grid', textKey: 'dungeonList' }]}
            />
            <div className="dungeon-grid">
              {(data.dungeons || []).map((d) => {
                const free = d.freeEntriesLeft > 0;
                const locked = !d.eligible;
                const cantPay = !free && data.ruhParasi < d.reentryCost;
                const limitReached = d.entriesLeft === 0;
                return (
                  <div key={d.key} className={`dungeon-entry${locked ? ' is-locked' : ''}`}>
                    <div className="dungeon-entry-media">
                      <img src={pub(d.image)} alt="" draggable={false} />
                      <span className="dungeon-entry-level">Lv {d.minLevel}{d.maxLevel ? `–${d.maxLevel}` : ''}</span>
                    </div>
                    <div className="dungeon-entry-body">
                      <h3>{isEn ? d.nameEn || d.name : d.name}</h3>
                      <p className="dungeon-entry-req">{d.maxLevel && data.level > d.maxLevel ? t('tooHigh', { max: d.maxLevel }) : locked ? t('locked', { level: d.minLevel }) : d.maxLevel ? t('levelRange', { min: d.minLevel, max: d.maxLevel }) : t('levelReq', { level: d.minLevel })}</p>
                      <p className="dungeon-entry-flow">
                        {t('flow', {
                          waves: d.waveCount,
                          interval: d.waveIntervalSeconds,
                          metin: translateGameName(d.metinName),
                          limit: d.metinTimeLimitSeconds,
                          boss: translateGameName(d.bossName),
                          bossLimit: d.bossTimeLimitSeconds,
                          total: d.waveCount * d.waveIntervalSeconds,
                        })}
                      </p>
                      <p className="dungeon-entry-reward">
                        {d.chestImage ? <img src={pub(d.chestImage)} alt="" /> : null}
                        {t('reward', { chest: translateGameName(d.chestName) || t('chest') })}
                      </p>
                      <p className="dungeon-entry-entries">
                        {free ? t('freeLeft', { count: d.freeEntriesLeft }) : t('reentryCost', { cost: d.reentryCost })}
                        {d.entriesLeft >= 0 ? ` · ${t('entriesLeft', { count: d.entriesLeft, max: d.maxEntriesPerDay })}` : ''}
                      </p>
                      <button
                        type="button"
                        className="auth-button dungeon-enter-btn"
                        style={authButtonStyle}
                        disabled={locked || cantPay || limitReached || Boolean(busyKey) || Boolean(data.activeRunId)}
                        onClick={() => enter(d.key)}
                      >
                        {busyKey === d.key ? t('entering') : limitReached ? t('limitReached') : free ? t('enter') : t('enterPaid', { cost: d.reentryCost })}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </AuthLayeredCard>
    </div>
  );
};

export default Dungeon;
